/// <amd-module name="VSSUI/Components/PivotBar/PivotBar" />
import { Dropdown, DropdownMenuItemType, IDropdownOption } from "OfficeFabric/Dropdown";
import { FocusZone, FocusZoneDirection } from "OfficeFabric/FocusZone";
import { BaseComponent, createRef, findIndex, IDisposable } from "OfficeFabric/Utilities";
import * as React from "react";
import { IObservableArray, IObservableValue, ObservableArray, ObservableLike } from "VSS/Core/Observable";
import { getId } from "VSS/Core/Util/Object";
import { Button } from "VSSUI/Button";
import { Accordion, IAccordionItem } from "VSSUI/DeprecatedAccordion";
import { DynamicLink } from "VSSUI/Dynamic";
import { FilterBar, IFilterBarProps } from "VSSUI/FilterBar";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeIdSelector } from "VSSUI/Util";
import { arrayEquals } from "VSSUI/Util";
import { childrenOfType, ReactChild } from "VSSUI/Utilities/ChildrenUtils";
import { FILTER_APPLIED_EVENT } from "VSSUI/Utilities/Filter";
import { getFriendlyDisplayValue } from "VSSUI/Utilities/FriendlyNumber";
import { ContributedItemArray, ContributedItemWithIdArray } from "VSSUI/Utilities/ItemContribution";
import { Location } from "VSSUI/Utilities/Position";
import { IViewOptionsValues, VIEW_OPTIONS_CHANGE_EVENT } from "VSSUI/Utilities/ViewOptions";
import * as VSSUI_Resources from "VSSUI/VSS.Resources.VSSUI";
import { IVssIconProps, VssIcon } from "VSSUI/VssIcon";
import {
    IDropDownPivotRenderingModeProps,
    IPivotBar,
    IPivotBarItem,
    IPivotBarProps,
    IPivotGroup,
    IPivotRenderingModeOptions,
    IVerticalPivotRenderingModeProps,
    IVssContributedPivotBarItem,
    PivotBarFocusItem,
    PivotBarSize,
    PivotRenderingMode
} from "../../Components/PivotBar/PivotBar.Props";
import { IPivotBarAction, IPivotBarViewAction, IVssPivotBarActionProvider } from "../../Components/PivotBar/PivotBarAction.Props";
import { PivotBarItem } from "../../Components/PivotBar/PivotBarItem";
import { PivotBarItemDeselectionBehavior, PivotChangedListener } from "../../Components/PivotBar/PivotBarItem.Props";
import { PivotCommandBar } from "../../Components/PivotBar/PivotCommandBar";
import { IPivotCommandBar } from "../../Components/PivotBar/PivotCommandBar.Props";
import { HubTileRegion } from "../../HubHeader";

import "VSS/LoaderPlugins/Css!VSSUI/Components/PivotBar/PivotBar";

const MAX_PIVOT_ITEM_WIDTH = 340;
/**
 * How long after load to wait (in milliseconds) to see if the selected pivot gets contributed
 * before giving up and selecting a different pivot instead.
 */
/** @deprecated */
export const MAX_PIVOT_WAIT = 1000;

/** @deprecated */
export interface IPivotBarState {
    selectedPivot?: string;
    items?: IPivotBarItemInternal[];
    commands?: IObservableArray<IPivotBarAction>[];
    viewActions?: IObservableArray<IPivotBarViewAction>[];
    showFilterBar?: boolean;
    hasAppliedFilter?: boolean;
    headerTop?: string;
    commandsMenuBarAriaLabel?: string;
}

/**
 * Internal structure used by PivotBar to keep track of its items (initial and contributed).
 *
 * Basically mirrors IPivotbarItemProps with the addition of id, child, and contribution.
 */
/** @deprecated */
export interface IPivotBarItemInternal extends IPivotBarItem {
    id: string;
    order: number;
    ariaLabel?: string;
    tooltip?: string;
    badgeCount?: number | string;
    maxItemWidth?: number;
    iconProps?: IVssIconProps;
    commands?: IPivotBarAction[] | IObservableArray<IPivotBarAction>;
    viewActions?: IPivotBarViewAction[] | IObservableArray<IPivotBarViewAction>;
    child?: PivotBarItem;
    commandProviders?: IVssPivotBarActionProvider[];
    viewActionProviders?: IVssPivotBarActionProvider[];
    deselectionBehavior?: PivotBarItemDeselectionBehavior;
    onRenderFilterBar?: () => React.ReactElement<IFilterBarProps>;
    hidden?: boolean;
    onBeforePivotChange?: PivotChangedListener;
    commandsMenuBarAriaLabel?: string;
}

interface IFilterBarSettings {
    filterBarElement: React.ReactElement<IFilterBarProps>;
    showInline: boolean;
}

/** @deprecated */
export type RuntimeProps<T> = Readonly<T & { children?: React.ReactNode }>;

/**
 * A PivotBar combines a Pivot component (tabs and tabbed content) along with a set of
 * commands (rendered after the pivots) and view actions (rendered on the far right).
 */
/** @deprecated */
export class PivotBar extends BaseComponent<IPivotBarProps, IPivotBarState> implements IPivotBar {
    public static defaultProps = { autoFocus: true } as IPivotBarProps;

    private _id: string;
    private _commandBar: IPivotCommandBar;

    private _contributedPivots: IObservableArray<IPivotBarItemInternal>[];
    private _subscribedToPivotObservables = false;
    private _actionsManager: PivotActionsManager;
    private _contributedPivotWait: number | null;
    /** True if we have successfully found the selected pivot after load */
    private _pivotSelected = false;
    /** Remembers which pivot items have been rendered */
    private _pivotItemsRendered: { [key: string]: boolean } = {};
    /**
     * Set this to true when the filter bar is toggled so that it will get focused after user
     * interaction, but not if it's visible on the initial render.
     */
    private _focusFilterBar?: boolean;

    private _pivotHeader = createRef<HTMLDivElement>();
    private _singleLineMode?: boolean;

    constructor(props: IPivotBarProps) {
        super(props);

        this._id = getId("PivotBar");

        this._setupPivotObservables(this.props);
        this._actionsManager = new PivotActionsManager();
        this._disposables.push(this._actionsManager);

        this.state = {
            items: this._getPivotBarItems(this.props)
        };
        // _buildInitialPivotState needs to have this.state.items set.
        this.state = {
            hasAppliedFilter: this.props.filter ? this.props.filter.hasChangesToReset() : false,
            items: this.state.items,
            ...this._buildInitialPivotState()
        };
    }

    public focus(focusItem: PivotBarFocusItem): void {
        if (this._commandBar) {
            switch (focusItem) {
                case PivotBarFocusItem.commands:
                    this._commandBar.focus();
                    break;
            }
        }
    }

    public selectPivot(pivotKey: string): boolean {
        if (!this._getPivotBarItemByItemKey(pivotKey)) {
            return false;
        }
        this._updateSelectedItem(pivotKey, null);
        return true;
    }

    /**
     * Gets the currently selected pivot
     */
    public getSelectedPivot(): IPivotBarItem {
        return this._getPivotBarItemByItemKey(this.state.selectedPivot);
    }

    /**
     * Allows the user to control the display of the filter bar when this function is called.
     * This function shows or hides the filter bar on demand.
     */
    public toggleFilterBar(): void {
        this._onShowFilterBarClicked();
    }

    /**
     * This gets executed when a container component re-renders PivotBar.
     * @param nextProps New props.
     */
    public componentWillReceiveProps(nextProps: RuntimeProps<IPivotBarProps>) {
        if (!arrayEquals(this.props.pivotProviders, nextProps.pivotProviders)) {
            this._setupPivotObservables(nextProps);
        }

        // Get the pivot items from the child elements
        const items = this._getPivotBarItems(nextProps);

        this.setState((prevState, props) => {
            return {
                items,
                ...this._buildInitialPivotState(props, items, true)
            };
        });
    }

    public componentDidMount() {
        if (this.props.viewOptions && this.props.showFilterBarViewOptionKey) {
            this._events.on(this.props.viewOptions, VIEW_OPTIONS_CHANGE_EVENT, this._onViewOptionChanged);
        }

        if (this.props.filter) {
            this.props.filter.subscribe(this._onFilterApplied, FILTER_APPLIED_EVENT);
        }

        this._updateHeaderTopIfNeeded();

        this._subscribeToPivotObservables();
    }

    public componentDidUpdate() {
        this._updateHeaderTopIfNeeded();
        if (this.props.onRenderComplete) {
            this.props.onRenderComplete();
        }
    }

    public componentWillUnmount() {
        if (this.props.viewOptions && this.props.showFilterBarViewOptionKey) {
            this._events.off(this.props.viewOptions, VIEW_OPTIONS_CHANGE_EVENT, this._onViewOptionChanged);
        }

        if (this.props.filter) {
            this.props.filter.unsubscribe(this._onFilterApplied, FILTER_APPLIED_EVENT);
        }

        if (this._contributedPivotWait) {
            clearTimeout(this._contributedPivotWait);
            this._contributedPivotWait = null;
        }

        this._unsubscribeFromPivotObservables();
    }

    public render(): JSX.Element {
        let {
            className,
            filter,
            showPivots,
            showFilterBarViewOptionKey,
            filterBarClassName,
            pivotRenderingModeOptions,
            isCommandBarSticky,
            isPivotBarContentScrollable,
            pivotBarSize = PivotBarSize.Compact,
            canBreadcrumbShrink,
            maxBreadcrumbWidth,
            pivotBarHeaderClassName
        } = this.props;
        let { showFilterBar, headerTop } = this.state;

        const children = React.Children.toArray(this.props.children) as React.ReactChild[] || [];
        const rawChildren = children.filter(
            c => typeof c !== "object" || (c.type !== PivotBarItem && c.type !== FilterBar && c.type !== HubTileRegion)
        );

        let hubTileRegion: JSX.Element = null;
        const hubTileRegionIndex = this._getHubTileRegionIndex(children);
        if (hubTileRegionIndex > -1) {
            hubTileRegion = children[hubTileRegionIndex] as React.ReactElement<any>;
        }

        let pivotItems: JSX.Element = null;
        let showHubTiles = hubTileRegionIndex > -1;

        if (showPivots === undefined) {
            showPivots = this.state.items && this.state.items.length > 1;
        } else {
            showHubTiles = showHubTiles && showPivots;
        }

        if (showPivots) {
            pivotItems = this._renderPivotItems(pivotRenderingModeOptions);
        }

        this._singleLineMode = !showPivots && !showHubTiles;
        const barClassName = this._singleLineMode ? "vss-PivotBar--bar-one-line" : "vss-PivotBar--bar-two-line";

        if (this.props.viewOptions && showFilterBarViewOptionKey) {
            showFilterBar = this.props.viewOptions.getViewOption(showFilterBarViewOptionKey);
        }

        let filterBarElement: React.ReactElement<IFilterBarProps>;
        const filterBarSettings = this._getFilterBarSettings(children);
        const showFilterInline = filterBarSettings && filterBarSettings.showInline;
        const shouldApplyStick = this.props.isPivotBarContentScrollable === false && !!isCommandBarSticky;

        if (filterBarSettings && (showFilterBar || filterBarSettings.showInline)) {
            filterBarElement = filterBarSettings.filterBarElement;
            filterBarElement = React.cloneElement<IFilterBarProps>(filterBarElement, {
                className: css(filterBarElement.props.className, filterBarClassName),
                filter: filterBarElement.props.filter || filter,
                onDismissClicked: this._onFilterBarDismissClicked,
                onMounted: filterBar => {
                    if (!showFilterInline && this._focusFilterBar) {
                        filterBar.focus();
                    }
                }
            });

            if (shouldApplyStick) {
                filterBarElement = <div className="vss-PivotBar--filter-sticky">{filterBarElement}</div>;
            }
        }

        // render a separator before the commands if we have items or we're in single line mode
        const prependSeparator = !!(pivotItems || this._singleLineMode);
        const stickyClassName = shouldApplyStick ? "sticky" : "";
        let breadcrumbContainerStyle = {};
        if (this._singleLineMode && maxBreadcrumbWidth) {
            breadcrumbContainerStyle = { maxWidth: `${maxBreadcrumbWidth}px` };
        }

        const rendered =
            pivotRenderingModeOptions && pivotRenderingModeOptions.mode === PivotRenderingMode.VerticalTabs ? (
                <div className={css("vss-PivotBar", className)}>
                    <div className={css(pivotBarHeaderClassName, "vss-PivotBar--header vertical")}>
                        {rawChildren}
                        {showHubTiles && hubTileRegion}
                    </div>
                    <div className="vss-PivotBar--pivotsContainer vertical">
                        {pivotItems}
                        <div className="vss-PivotBar--barAndContentContainer">
                            <div className={css("vss-PivotBar--bar", pivotBarSize)}>
                                {this._renderCommandBar(showFilterInline ? filterBarElement : null, !!filterBarSettings, false)}
                                {showFilterInline ? null : filterBarElement}
                            </div>
                            {this._renderContent()}
                        </div>
                    </div>
                </div>
            ) : (
                <div className={css("vss-PivotBar", className)}>
                    <div
                        className={css(barClassName, stickyClassName, !showFilterBar && "bottom-border")}
                        style={{ top: this._singleLineMode ? 0 : headerTop }}
                    >
                        <div
                            className={css(
                                pivotBarHeaderClassName,
                                "vss-PivotBar--header",
                                canBreadcrumbShrink && this._singleLineMode && "can-shrink"
                            )}
                            ref={this._pivotHeader}
                            style={breadcrumbContainerStyle}
                        >
                            {rawChildren}
                            {showHubTiles && hubTileRegion}
                        </div>
                        <div className={css("vss-PivotBar--bar", pivotBarSize)}>
                            {pivotItems}
                            {this._renderCommandBar(showFilterInline ? filterBarElement : null, !!filterBarSettings, prependSeparator)}
                        </div>
                    </div>
                    {showFilterInline ? null : filterBarElement}
                    {this._renderContent()}
                </div>
            );

        return rendered;
    }

    private _onFilterBarDismissClicked = (): void => {
        const { showFilterBarViewOptionKey, viewOptions } = this.props;

        if (viewOptions && showFilterBarViewOptionKey) {
            this.props.viewOptions.setViewOption(showFilterBarViewOptionKey, false);
        }
        if (this._commandBar) {
            this._commandBar.focus();
        }

        this.setState({ showFilterBar: false });
    };

    private _updateHeaderTopIfNeeded(): void {
        if (
            this.props.isPivotBarContentScrollable === false &&
            !!this.props.isCommandBarSticky &&
            this.state.headerTop === undefined &&
            !this._singleLineMode
        ) {
            this.setState({ headerTop: `-${this._pivotHeader.current.clientHeight}px` });
        }
    }

    private _renderPivotItems(pivotRenderingModeOptions: IPivotRenderingModeOptions): JSX.Element {
        if (pivotRenderingModeOptions && pivotRenderingModeOptions.mode === PivotRenderingMode.DropDown) {
            return this._renderDropdown(pivotRenderingModeOptions.props);
        } else if (pivotRenderingModeOptions && pivotRenderingModeOptions.mode === PivotRenderingMode.VerticalTabs) {
            return this._renderVerticalTabs();
        } else {
            return this._renderHorizontalTabs();
        }
    }

    private _renderDropdown(props: IDropDownPivotRenderingModeProps): JSX.Element {
        const options = [];
        if (this.state.items.length > 0) {
            let lastGroupKey = this.state.items[0].groupKey;
            for (const item of this.state.items) {
                const { name, itemKey, groupKey } = item;

                if (lastGroupKey !== groupKey) {
                    options.push({ key: `${groupKey}-separator`, text: "-", itemType: DropdownMenuItemType.Divider });
                    lastGroupKey = groupKey;
                }

                options.push({ key: itemKey, text: name });
            }
        }

        return (
            <Dropdown
                ariaLabel={VSSUI_Resources.PivotItemAriaLabel}
                className={"vss-PivotBar--dropdown"}
                selectedKey={this.state.selectedPivot}
                options={options}
                onChanged={this._onDropDownSelected}
                dropdownWidth={props.dropdownWidth}
            />
        );
    }

    private _onDropDownSelected = (option: IDropdownOption): void => {
        const itemKey = option.key as string;
        this._updateSelectedItem(itemKey, null);
    };

    private _pivotCompare(a?: number, b?: number) {
        return (a === undefined ? 100 : a) - (b === undefined ? 100 : b);
    }

    private _renderHorizontalTabs(): JSX.Element {
        const pivots = this.state.items ? this.state.items.sort((a, b) => this._pivotCompare(a.order, b.order)) : null;
        const { pivotBarSize = PivotBarSize.Compact, headerAriaLabel } = this.props;

        /*
         * Setting both the key and the defaultActiveElement props of FocusZone to force the FocusZone to
         * re-render and set the selectedPivot as the active element. This matters when an experience
         * programmatically changes the active pivot outside of directly interacting with the PivotBar itself
         * (or in other words PivotBar is rendered with a different selectedPivot in its new props).
         */
        let defaultActiveElement: string | undefined;
        if (this.state.items && this.state.selectedPivot && this.props.autoFocus) {
            const selectedPivotBarItem = this._getPivotBarItemByItemKey(this.state.selectedPivot);
            if (selectedPivotBarItem) {
                defaultActiveElement = getSafeIdSelector(selectedPivotBarItem.id);
            }
        }

        return (
            <div role="navigation" aria-label={headerAriaLabel}>
                <FocusZone
                    key={defaultActiveElement}
                    className={css("vss-PivotBar--pivots", pivotBarSize)}
                    role="tablist"
                    direction={FocusZoneDirection.horizontal}
                    defaultActiveElement={defaultActiveElement}
                >
                    {pivots && pivots.map(this._renderPivotItem)}
                </FocusZone>
            </div>
        );
    }

    private _renderVerticalTabs(): JSX.Element {
        // Get a sorted list of pivot groups to pass to the nav
        let pivotGroups: IPivotGroup[] = [];
        const pivotGroupMap: { [groupKey: string]: boolean } = {};
        let pivotNavAriaLabel;
        if (this.props.pivotRenderingModeOptions && this.props.pivotRenderingModeOptions.props) {
            const { groups, navAriaLabel } = this.props.pivotRenderingModeOptions.props as IVerticalPivotRenderingModeProps;

            if (groups) {
                for (const group of groups) {
                    pivotGroupMap[group.key] = true;
                }
                pivotGroups = groups.sort((groupA, groupB) => this._pivotCompare(groupA.rank, groupB.rank));
            }

            pivotNavAriaLabel = navAriaLabel;
        }

        // Map pivot bar items to their groups.
        // If an item has no groupKey, or its group is not defined,
        // it will be rendered at the end without a group.
        const ungroupedItems: IPivotBarItemInternal[] = [];
        const groupedItems = this.state.items.reduce(
            (reduced, item) => {
                if (item.groupKey && pivotGroupMap[item.groupKey]) {
                    (reduced[item.groupKey] = reduced[item.groupKey] || []).push(item);
                } else {
                    ungroupedItems.push(item);
                }
                return reduced;
            },
            {} as { [groupKey: string]: IPivotBarItemInternal[] }
        );

        const groups: JSX.Element[] = [
            <Accordion
                key="PivotBarVerticalAccordion"
                className="pivot-bar--accordion"
                initiallyExpandAll={true}
                items={pivotGroups
                    .map(group => {
                        let item: IAccordionItem;
                        if (groupedItems[group.key]) {
                            groupedItems[group.key].sort((a, b) => this._pivotCompare(a.order, b.order));
                            item = {
                                key: group.key,
                                title: group.name,
                                onRenderContent: () => {
                                    return (
                                        <div key={item.key} role="tablist">
                                            {groupedItems[group.key].map((pivotItem, index) =>
                                                this._renderVerticalPivotItem(pivotItem, index, groupedItems[group.key].length)
                                            )}
                                        </div>
                                    );
                                }
                            };
                        }
                        return item;
                    })
                    .filter(item => {
                        return item !== undefined;
                    })}
            />
        ];

        groups.push(
            <div className="pivot-bar--ungrouped" key="PivotBarVerticalUngrouped" role="tablist">
                {ungroupedItems.map((pivotItem, index) => this._renderVerticalPivotItem(pivotItem, index, ungroupedItems.length))}
            </div>
        );

        /*
         * Setting both the key and the defaultActiveElement props of FocusZone to force the FocusZone to
         * re-render and set the selectedPivot as the active element. This matters when an experience
         * programmatically changes the active pivot outside of directly interacting with the PivotBar itself
         * (or in other words PivotBar is rendered with a different selectedPivot in its new props).
         */
        let defaultActiveElement: string | undefined;
        if (this.state.items && this.state.selectedPivot) {
            const selectedPivotBarItem = this._getPivotBarItemByItemKey(this.state.selectedPivot);
            if (selectedPivotBarItem) {
                defaultActiveElement = getSafeIdSelector(selectedPivotBarItem.id);
            }
        }

        return (
            <FocusZone
                key={defaultActiveElement}
                direction={FocusZoneDirection.vertical}
                className="vss-PivotBar--pivots vertical flex-column"
                aria-label={pivotNavAriaLabel}
                defaultActiveElement={defaultActiveElement}
            >
                {groups}
            </FocusZone>
        );
    }

    private _renderVerticalPivotItem(item: IPivotBarItemInternal, index: number, groupLength: number): JSX.Element {
        const itemKey = item.itemKey;
        const isSelected = this.state.selectedPivot === itemKey;

        return (
            <DynamicLink
                url={item.url}
                className={css("vss-PivotBar--itemLink-v", item.child && item.child.props.headerClassName)}
                tabIndex={-1}
                role="presentation"
                data-is-focusable={false}
                key={itemKey}
            >
                <Button
                    subtle={true}
                    id={item.id}
                    tooltipProps={{
                                    text: item.tooltip
                                 }}
                    className={css("vss-PivotBar--button-v", isSelected ? "selected" : "")}
                    onClick={this._onPivotClick.bind(this, itemKey)}
                    ariaLabel={item.ariaLabel}
                    role="tab"
                    ariaSelected={isSelected}
                    ariaPosInSet={index + 1}
                    ariaSetSize={groupLength}
                >
                    <VssIcon className={css("pivotbar-pivot--icon-v", item.iconProps && item.iconProps.className)} iconName={item.iconProps && item.iconProps.iconName} />
                    <Tooltip
                        anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                        overflowOnly={true}
                        text={item.name}
                        tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
                    >
                        <div className="flex flex-grow text-ellipsis">{item.name}</div>
                    </Tooltip>
                </Button>
            </DynamicLink>
        );
    }

    private _renderPivotItem = (item: IPivotBarItemInternal, index: number): JSX.Element => {
        const itemKey = item.itemKey;
        const isSelected = this.state.selectedPivot === itemKey;

        return (
            <DynamicLink
                url={item.url}
                className={css("vss-PivotBar--itemLink", item.child && item.child.props.headerClassName)}
                tabIndex={-1}
                role="presentation"
                data-is-focusable={false}
                key={itemKey}
            >
                <Button
                    id={item.id}
                    className={css("vss-PivotBar--button", isSelected ? "selected" : "")}
                    onClick={this._onPivotClick.bind(this, itemKey)}
                    ariaLabel={item.ariaLabel}
                    role="tab"
                    ariaSelected={isSelected}
                    ariaPosInSet={index + 1}
                    ariaSetSize={this.state.items.length}
                >
                    {this._renderPivotItemContent(item)}
                </Button>
            </DynamicLink>
        );
    };

    private _renderPivotItemContent(item: IPivotBarItemInternal): JSX.Element {
        const { name, iconProps, badgeCount, maxItemWidth } = item;

        let badgeDisplayValue: string | undefined = undefined;
        let badgeTooltip: string | undefined = undefined;
        if (typeof badgeCount === "string") {
            badgeDisplayValue = badgeCount;
        } else if (typeof badgeCount === "number") {
            badgeDisplayValue = getFriendlyDisplayValue(badgeCount);
            if (badgeDisplayValue !== badgeCount.toString()) {
                badgeTooltip = badgeCount.toString();
            }
        }

        let badgeElement: JSX.Element | undefined = undefined;
        if (badgeDisplayValue) {
            badgeElement = <span className="badge">{badgeDisplayValue}</span>;
            if (badgeTooltip) {
                badgeElement = <Tooltip text={badgeTooltip}>{badgeElement}</Tooltip>;
            }
        }

        return (
            <span className="innerContainer">
                {iconProps !== undefined && <VssIcon className="icon" {...iconProps} />}
                {name !== undefined && (
                    <Tooltip overflowOnly={true} text={name}>
                        <span className="text" style={{ maxWidth: maxItemWidth || MAX_PIVOT_ITEM_WIDTH }}>
                            {name}
                        </span>
                    </Tooltip>
                )}
                {badgeElement}
            </span>
        );
    }

    private _renderCommandBar(filterBar: JSX.Element, showFilterAction: boolean, prependSeparator: boolean) {
        return (
            <PivotCommandBar
                componentRef={commandBar => {
                    this._commandBar = commandBar;
                }}
                className="vss-PivotBar--commandBar"
                commands={this.state.commands}
                viewActions={this.state.viewActions}
                filterBar={filterBar}
                showFilterAction={this.props.hideFilterAction !== undefined ? false : showFilterAction}
                showFilterBarViewOptionKey={this.props.showFilterBarViewOptionKey}
                hasAppliedFilter={this.state.hasAppliedFilter}
                prependSeparator={prependSeparator}
                viewOptions={this.props.viewOptions}
                onShowFilterBarClicked={this._onShowFilterBarClicked}
                role={"region"}
                ariaLabel={this.props.headerAriaLabel}
                commandsMenuBarAriaLabel={this.state.commandsMenuBarAriaLabel}
                inlineFilterAriaLabel={this.props.inlineFilterAriaLabel}
            />
        );
    }

    private _renderContent() {
        const content: JSX.Element[] = [];
        const selectedPivot = (this.state.selectedPivot || "").toUpperCase();
        const isContentScrollable = this.props.isPivotBarContentScrollable !== undefined ? this.props.isPivotBarContentScrollable : true;
        for (const item of this.state.items) {
            const itemKeyUpperCase = item.itemKey.toUpperCase();
            const isSelected = selectedPivot === itemKeyUpperCase;
            const shouldHide =
                !isSelected &&
                item.deselectionBehavior === PivotBarItemDeselectionBehavior.Hide &&
                this._pivotItemsRendered[itemKeyUpperCase] === true;

            if (isSelected || shouldHide) {
                content.push(
                    <div
                        key={itemKeyUpperCase}
                        className={css(
                            "vss-PivotBar--content",
                            !isContentScrollable && "vss-PivotBar--content-unscrollable",
                            shouldHide && "hide-content",
                            this.props.pivotBarContentClassName
                        )}
                        role="tabpanel"
                        tabIndex={-1}
                        data-is-scrollable={isContentScrollable}
                        aria-label={this.props.pivotBarContentAriaLabel}
                    >
                        {item && (item.child || item.contributedItem.render(css("vss-PivotBarItem", "content-host")))}
                    </div>
                );
                this._pivotItemsRendered[itemKeyUpperCase] = true;
            }
        }

        return content;
    }

    private _onShowFilterBarClicked = (): void => {
        this.setState({
            showFilterBar: !this.state.showFilterBar
        });
    };

    private _onFilterApplied = () => {
        const hasAppliedFilter = this.props.filter.hasChangesToReset();
        if (hasAppliedFilter !== this.state.hasAppliedFilter) {
            this.setState({
                hasAppliedFilter: hasAppliedFilter
            });
        }
    };

    private _onContributedPivotsChanged = (): void => {
        this.setState({
            items: this._getPivotBarItems(this.props)
        });

        if (!this._pivotSelected) {
            this.setState(this._buildInitialPivotState());
        } else if (this.state.selectedPivot && !this.getSelectedPivot()) {
            // our selected pivot has disappeared, choose a new one
            let selectedPivot: string;
            if (this.props.initialSelectedPivot && this._getPivotBarItemByItemKey(this.props.initialSelectedPivot)) {
                selectedPivot = this.props.initialSelectedPivot;
            } else if (this.state.items && this.state.items.length > 0) {
                selectedPivot = this.state.items[0].itemKey;
            }

            if (selectedPivot) {
                this.selectPivot(selectedPivot);
            }
        }
    };

    private _onViewOptionChanged = (changedOptions: IViewOptionsValues) => {
        const { showFilterBarViewOptionKey } = this.props;
        const { viewActions } = this.state;
        let needsUpdate = false;

        if (showFilterBarViewOptionKey in changedOptions) {
            this._focusFilterBar = true;
            needsUpdate = true;
        }

        if (!needsUpdate) {
            const allViewActions: IPivotBarViewAction[] = [];
            if (viewActions) {
                viewActions.forEach(va => allViewActions.push(...va.value));
            }

            for (const changedOptionKey in changedOptions) {
                if (findIndex(allViewActions, a => a.key === changedOptionKey) >= 0) {
                    needsUpdate = true;
                    break;
                }
            }
        }

        if (needsUpdate) {
            this.forceUpdate();
        }
    };

    private _makeIPivotBarItemFromContribution = (item: IVssContributedPivotBarItem, index: number): IPivotBarItemInternal => {
        return {
            contributedItem: item,
            id: this._makeContributedPivotBarItemId(item, index),
            itemKey: item.itemKey || item.id,
            groupKey: item.groupKey,
            name: item.text,
            order: item.order,
            ariaLabel: item.ariaLabel,
            onRenderFilterBar: item.renderFilterBar,
            commandProviders: item.commandProviders,
            viewActionProviders: item.viewActionProviders, // TODO: all the rest
            // iconProps: null,
            badgeCount: item.badgeCount,
            url: item["url"],
            hidden: item.hidden,
            onBeforePivotChange: item.onBeforePivotChange
        };
    };

    private _makePivotBarItemId(child: ReactChild<PivotBarItem>): string {
        return `${this._id}-Tab${child.index}`;
    }

    private _makeContributedPivotBarItemId(item: IVssContributedPivotBarItem, index: number): string {
        return `${this._id}-ContributedTab${index}`;
    }

    private _getPivotBarItemByItemKey(itemKey: string, items = this.state.items): IPivotBarItemInternal {
        const itemKeyUpperCase = (itemKey || "").toUpperCase();
        const index = findIndex(items, i => i.itemKey.toUpperCase() === itemKeyUpperCase);
        return index >= 0 ? items[index] : undefined;
    }

    /**
     * Extracts the pivot items from the child elements.
     * @param props PivotBar properties.
     */
    private _getPivotBarItems(props: RuntimeProps<IPivotBarProps>): IPivotBarItemInternal[] {
        const groupedItems: { [key: string]: IPivotBarItemInternal[] } = {};
        const pivotChildren = childrenOfType(props.children as React.ReactChild[], PivotBarItem);

        const pushItem = (item: IPivotBarItemInternal) => {
            const groupKey = item.groupKey || "";
            let items = groupedItems[groupKey];
            if (!items) {
                items = [];
                groupedItems[groupKey] = items;
            }

            items.push(item);
        };

        let order = 100;
        for (const pivot of pivotChildren) {
            const props = pivot.item.props;
            if (!props.hidden) {
                pushItem({
                    child: pivot.item,
                    id: this._makePivotBarItemId(pivot),
                    itemKey: props.itemKey,
                    name: props.name,
			        tooltip: props.tooltip,
                    groupKey: props.groupKey,
                    order: props.order != undefined ? props.order : order++,
                    ariaLabel: props.ariaLabel,
                    iconProps: props.iconProps,
                    badgeCount: props.badgeCount,
                    maxItemWidth: props.maxItemWidth,
                    commands: props.commands,
                    viewActions: props.viewActions,
                    commandProviders: props.commandProviders,
                    viewActionProviders: props.viewActionProviders,
                    deselectionBehavior: props.deselectionBehavior,
                    url: props.url,
                    onBeforePivotChange: props.onBeforePivotChange,
                    onRenderFilterBar: props.onRenderFilterBar,
                    commandsMenuBarAriaLabel: props.commandsMenuBarAriaLabel
                });
            }
        }

        // Add contributed pivots from the observed collections
        this._contributedPivots.forEach(pivot =>
            pivot.value.forEach(item => {
                if (!item.hidden) {
                    pushItem(item);
                }
            })
        );

        return Object.keys(groupedItems).reduce((acc, key) => {
            groupedItems[key].forEach(item => acc.push(item));
            return acc;
        }, []);
    }

    private _setupPivotObservables(props: RuntimeProps<IPivotBarProps>): void {
        let index = 0;
        const wasSubscribed = this._subscribedToPivotObservables;

        if (wasSubscribed) {
            this._unsubscribeFromPivotObservables();
        }
        this._contributedPivots = [];
        if (props.pivotProviders) {
            props.pivotProviders.forEach(pivotProvider => {
                const contributedItems = new ContributedItemWithIdArray<IVssContributedPivotBarItem, IPivotBarItemInternal>(
                    pivotProvider,
                    item => this._makeIPivotBarItemFromContribution(item, index++),
                    this._getPivotBarItemId
                );

                this._contributedPivots.push(contributedItems);
            });
            if (wasSubscribed) {
                this._subscribeToPivotObservables();
            }
        }
    }

    private _unsubscribeFromPivotObservables(): void {
        this._contributedPivots.forEach(pivots => pivots.unsubscribe(this._onContributedPivotsChanged));
        this._subscribedToPivotObservables = false;
    }

    private _subscribeToPivotObservables(): void {
        this._contributedPivots.forEach(pivots => pivots.subscribe(this._onContributedPivotsChanged));
        this._subscribedToPivotObservables = true;
    }

    private _getPivotBarItemId = (item: IPivotBarItemInternal): string => {
        return item.contributedItem ? item.contributedItem.id : item.itemKey || item.id;
    };

    private _getFilterBarSettings(children: React.ReactChild[]): IFilterBarSettings | undefined {
        let { onRenderFilterBar, showFilterBarInline } = this.props;

        let filterBarElement: React.ReactElement<IFilterBarProps>;

        // Check if the pivot itself has a custom filter bar
        const selectedPivot = this._getPivotBarItemByItemKey(this.state.selectedPivot);
        if (selectedPivot && selectedPivot.onRenderFilterBar) {
            filterBarElement = selectedPivot.onRenderFilterBar();
        }

        // Invoke a custom onRenderFilterBar prop which can return a custom filter bar (e.g. not of type FilterBar)
        if (!filterBarElement && onRenderFilterBar) {
            filterBarElement = onRenderFilterBar();
        }

        // Look for a child FilterBar if none was supplied by the onRenderFilterBar prop
        if (!filterBarElement) {
            const filterBarIndex = findIndex(children, child => typeof child === "object" && child.type === FilterBar);
            if (filterBarIndex >= 0) {
                const childFilterBarElement = children[filterBarIndex] as React.ReactElement<any>;

                // Make sure that the child FilterBar has one or more child filters
                const filterBarChildren = React.Children.toArray(childFilterBarElement.props.children as React.ReactChild[]) as React.ReactChild[];
                if (filterBarChildren && filterBarChildren.length >= 1) {
                    filterBarElement = childFilterBarElement;

                    // Show the filterbar inline if it has only one filter which is a text filter
                    if (filterBarChildren.length === 1 && !showFilterBarInline) {
                        const firstFilterBarChild = filterBarChildren[0];
                        if (typeof firstFilterBarChild === "object" && firstFilterBarChild.props.isTextItem === true) {
                            showFilterBarInline = true;
                        }
                    }
                }
            }
        }

        if (filterBarElement) {
            return {
                filterBarElement,
                showInline: showFilterBarInline
            };
        }
    }

    private _getHubTileRegionIndex(children: React.ReactChild[]): number {
        return findIndex(children, child => typeof child === "object" && child.type === HubTileRegion);
    }

    /**
     * Determines whether the key exists in the pivot items or not.
     */
    private _isKeyValid(itemKey: string | undefined, items: IPivotBarItemInternal[] = this.state.items) {
        return itemKey !== undefined && this._getPivotBarItemByItemKey(itemKey, items) !== undefined;
    }

    private _buildInitialPivotState(
        props: RuntimeProps<IPivotBarProps> = this.props,
        items?: IPivotBarItemInternal[],
        resetActions: boolean = false
    ): IPivotBarState {
        const getFirstPivot = () => {
            const currentItems = items || this._getPivotBarItems(props);
            if (currentItems.length > 0) {
                return currentItems[0].itemKey;
            }
            return null;
        };
        let selectedPivot: string = null;

        if (!props.initialSelectedPivot && !props.selectedPivot) {
            // if there is no selected pivot, default to the first pivot
            selectedPivot = getFirstPivot();
        }

        if (!selectedPivot && props.initialSelectedPivot && this._isKeyValid(props.initialSelectedPivot)) {
            selectedPivot = props.initialSelectedPivot;
        }

        if (!selectedPivot && props.selectedPivot && this._isKeyValid(props.selectedPivot)) {
            selectedPivot = props.selectedPivot;
        }

        if (!this._isKeyValid(selectedPivot, items)) {
            // display no pivot while we wait to see if the pivot will be contributed
            selectedPivot = null;
            if (!this._contributedPivotWait) {
                this._contributedPivotWait = setTimeout(() => {
                    if (!this._pivotSelected) {
                        // if the selected pivot has not yet been contributed, select the first pivot
                        selectedPivot = getFirstPivot();
                        if (selectedPivot) {
                            this._updateSelectedItem(selectedPivot, null);
                        }
                    }
                    this._contributedPivotWait = null;
                }, MAX_PIVOT_WAIT);
            }
        }

        this._pivotSelected = selectedPivot !== null;

        return this._buildNewPivotState(props, selectedPivot, items, resetActions);
    }

    private _buildNewPivotState(
        props: IPivotBarProps,
        selectedPivotItemKey: string,
        items?: IPivotBarItemInternal[],
        resetActions: boolean = false
    ): IPivotBarState {
        const selectedPivot = this._getPivotBarItemByItemKey(selectedPivotItemKey, items);
        const selectedPivotAvailable = selectedPivotItemKey && selectedPivot;

        return {
            selectedPivot: selectedPivotAvailable ? selectedPivot.itemKey : "",
            commands: selectedPivotAvailable ? this._actionsManager.getCommandObservables(props.commands, selectedPivot, resetActions) : [],
            viewActions: selectedPivotAvailable ? this._actionsManager.getViewActionObservables(props.viewActions, selectedPivot, resetActions) : [],
            commandsMenuBarAriaLabel: selectedPivotAvailable ? selectedPivot.commandsMenuBarAriaLabel : undefined
        };
    }

    /**
     * Handles the onClick event on the pivot button.
     */
    private _onPivotClick(itemKey: string, ev: React.MouseEvent<HTMLElement>) {
        let updatePivot = true;

        // If ctrl-click is pressed, and there is a URL specified for this item, then
        // don't handle the click here, allowing the browser to perform a navigation
        // (i.e. open in a new tab/window)
        if (ev.ctrlKey) {
            const item = this._getPivotBarItemByItemKey(itemKey);
            if (item && item.url) {
                if (typeof item.url === "string" || item.url.value) {
                    updatePivot = false;
                }
            }
        }

        if (updatePivot) {
            ev.preventDefault();
            this._updateSelectedItem(itemKey, ev);
        }
    }

    /**
     * Updates the state with the new selected pivot.
     */
    private _updateSelectedItem(itemKey: string, ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | null) {
        const item = this._getPivotBarItemByItemKey(itemKey);
        if (item && (this.props.onBeforePivotChange || item.onBeforePivotChange)) {
            const itemUrl = item && item.url && (typeof item.url === "string" ? item.url : item.url.value);

            if (item.onBeforePivotChange && !item.onBeforePivotChange(ev, itemKey, itemUrl)) {
                return;
            }

            if (this.props.onBeforePivotChange && !this.props.onBeforePivotChange(ev, itemKey, itemUrl)) {
                return;
            }
        }

        const props = this.props;
        this._pivotSelected = true;
        this.setState(this._buildNewPivotState(props, itemKey));

        // Update notifiers
        if (props.onPivotClicked) {
            props.onPivotClicked(ev, itemKey);
        }
    }
}

class PivotActionsManager implements IDisposable {
    private _commands: { [pivotKey: string]: IObservableArray<IPivotBarAction>[] } = {};
    private _viewActions: { [pivotKey: string]: IObservableArray<IPivotBarViewAction>[] } = {};

    public getCommandObservables(
        pivotBarCommands: IPivotBarAction[] | IObservableArray<IPivotBarAction>,
        selectedPivot: IPivotBarItemInternal,
        reset: boolean = false
    ): IObservableArray<IPivotBarAction>[] {
        const selectedPivotId = selectedPivot.id;

        if (reset === true) {
            delete this._commands[selectedPivotId];
        }

        if (!this._commands[selectedPivotId]) {
            const commands: IObservableArray<IPivotBarAction>[] = [];
            this._commands[selectedPivotId] = commands;

            // Add hub commands first
            commands.push(
                ObservableLike.isObservable(pivotBarCommands)
                    ? (pivotBarCommands as IObservableArray<IPivotBarAction>)
                    : new ObservableArray(pivotBarCommands as IPivotBarAction[])
            );

            // Add commands from the selected pivot
            commands.push(
                ObservableLike.isObservable(selectedPivot.commands)
                    ? (selectedPivot.commands as IObservableArray<IPivotBarAction>)
                    : new ObservableArray(selectedPivot.commands as IPivotBarAction[])
            );

            // Add contributed commands
            if (selectedPivot.commandProviders) {
                selectedPivot.commandProviders.forEach(commandProvider => {
                    const contributedCommands = new ContributedItemWithIdArray<IPivotBarAction, IPivotBarAction>(
                        commandProvider,
                        command => command,
                        this._getPivotActionItemId
                    );

                    commands.push(contributedCommands);
                });
            }
        }

        return this._commands[selectedPivotId];
    }

    public getViewActionObservables(
        pivotBarViewActions: IPivotBarViewAction[] | IObservableArray<IPivotBarViewAction>,
        selectedPivot: IPivotBarItemInternal,
        reset: boolean = false
    ): IObservableArray<IPivotBarViewAction>[] {
        const selectedPivotId = selectedPivot.id;

        if (reset === true) {
            delete this._viewActions[selectedPivotId];
        }

        if (!this._viewActions[selectedPivotId]) {
            const viewActions: IObservableArray<IPivotBarViewAction>[] = [];
            this._viewActions[selectedPivotId] = viewActions;

            // Add view actions from the selected pivot first
            viewActions.push(
                ObservableLike.isObservable(selectedPivot.viewActions)
                    ? (selectedPivot.viewActions as IObservableArray<IPivotBarViewAction>)
                    : new ObservableArray(selectedPivot.viewActions as IPivotBarViewAction[])
            );

            // Add hub view actions
            viewActions.push(
                ObservableLike.isObservable(pivotBarViewActions)
                    ? (pivotBarViewActions as IObservableArray<IPivotBarViewAction>)
                    : new ObservableArray(pivotBarViewActions as IPivotBarViewAction[])
            );

            // Add contributed view actions
            if (selectedPivot.viewActionProviders) {
                selectedPivot.viewActionProviders.forEach(viewActionProvider => {
                    const contributedViewActions = new ContributedItemArray<IPivotBarViewAction, IPivotBarViewAction>(
                        viewActionProvider,
                        viewAction => viewAction
                    );

                    viewActions.push(contributedViewActions);
                });
            }
        }

        return this._viewActions[selectedPivotId];
    }

    public dispose(): void {
        this._commands = null;
        this._viewActions = null;
    }

    private _getPivotActionItemId = (item: IPivotBarAction): string => {
        return item.key;
    };
}
